import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { conditions, searchConditions } from '../data/conditions';
import { ClinicalCondition, Specialty, ResourceLevel } from '../types';

const SPECIALTIES: Specialty[] = [
  'General Practice', 'Pediatrics', 'Obstetrics', 'Internal Medicine', 'Surgery',
  'Emergency Medicine', 'Infectious Disease', 'Cardiology', 'Pulmonology', 'Neurology',
  'Endocrinology', 'Gastroenterology', 'Nephrology', 'Dermatology', 'Psychiatry',
  'Ophthalmology', 'ENT', 'Orthopedics', 'Oncology', 'Hematology'
];

const RESOURCE_LEVELS: ResourceLevel[] = ['BASIC', 'INTERMEDIATE', 'ADVANCED'];

export default function ConditionsPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>(searchParams.get('specialty') || '');
  const [selectedLevel, setSelectedLevel] = useState<string>(searchParams.get('level') || '');
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(searchParams.get('emergency') === 'true');

  const filteredConditions = useMemo(() => {
    let results: ClinicalCondition[] = conditions;
    
    if (query) {
      results = searchConditions(query);
    }
    
    if (selectedSpecialty) {
      results = results.filter(c => c.specialty === selectedSpecialty);
    }
    
    if (selectedLevel) {
      results = results.filter(c => c.resourceLevel === selectedLevel);
    }
    
    if (showEmergencyOnly) {
      results = results.filter(c => c.isEmergency);
    }
    
    return results;
  }, [query, selectedSpecialty, selectedLevel, showEmergencyOnly]);

  const getLevelColor = (level: ResourceLevel) => {
    switch (level) {
      case 'BASIC': return 'bg-green-100 text-green-700';
      case 'INTERMEDIATE': return 'bg-yellow-100 text-yellow-700';
      case 'ADVANCED': return 'bg-red-100 text-red-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">{t('clinicalConditions')}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Specialty Filter */}
            <select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
              className="px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('allSpecialties')}</option>
              {SPECIALTIES.map(spec => (
                <option key={spec} value={spec}>{spec}</option>
              ))}
            </select>

            {/* Level Filter */}
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('allLevels')}</option>
              {RESOURCE_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>

            {/* Emergency Toggle */}
            <label className="flex items-center gap-2 px-4 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showEmergencyOnly}
                onChange={(e) => setShowEmergencyOnly(e.target.checked)}
                className="w-5 h-5 rounded text-red-600 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-red-600">Emergencies Only</span>
            </label>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-gray-600 mb-4">
          {t('showing')} {filteredConditions.length} {t('of')} {conditions.length} {t('results')}
        </p>

        {/* Conditions Grid */}
        {filteredConditions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredConditions.map((condition) => (
              <Link
                key={condition.id}
                to={`/conditions/${condition.slug}`}
                className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-blue-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                    {condition.name}
                  </h3>
                  {condition.isEmergency && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
                      URGENT
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-blue-600 mb-1">{condition.specialty}</p>
                <p className="text-xs text-gray-500 mb-3">{condition.bodySystem}</p>
                
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${getLevelColor(condition.resourceLevel)}`}>
                    {condition.resourceLevel}
                  </span>
                  <span className="text-xs text-gray-400">ICD: {condition.icdCode}</span>
                </div>
                
                <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                  {condition.overview}
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">{t('noResults')}</p>
            <p className="text-gray-400">{t('tryAdjusting')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
